import { motion } from "framer-motion";
import { CalendarDays, MapPin, Phone, ExternalLink } from "lucide-react";
import { NAKODA_LOGO, CROWD_IMG } from "@/data";

const fade = {
  hidden: { opacity: 0, y: 40 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1], delay: i * 0.08 },
  }),
};

const mapsQuery = encodeURIComponent(
  "Arcor Hotels, K.R.S. Road Junction, Industrial Area Metagalli, Mysuru"
);

export default function EventDetails() {
  return (
    <section id="event" className="relative bg-ink py-24 md:py-32" data-testid="event-details">
      <div className="mx-auto max-w-7xl px-6 md:px-10">
        <motion.div
          variants={fade}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-100px" }}
          className="mb-14 max-w-3xl"
        >
          <span className="overline text-orange">The Event</span>
          <h2 className="mt-4 font-display text-4xl font-semibold tracking-tighter text-white sm:text-5xl lg:text-6xl">
            Major CCTV &amp; surveillance brands, <span className="text-chrome">under one roof.</span>
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-12">
          {/* Dates */}
          <motion.div
            custom={0} variants={fade} initial="hidden" whileInView="show" viewport={{ once: true }}
            className="group flex flex-col justify-between rounded-sm border border-white/10 bg-card p-8 transition-colors duration-300 hover:border-orange/50 md:col-span-4"
            data-testid="event-dates"
          >
            <CalendarDays className="h-7 w-7 text-orange" />
            <div className="mt-10">
              <div className="overline text-muted-foreground">Dates</div>
              <div className="mt-2 font-display text-3xl font-semibold text-white">30 &amp; 31</div>
              <div className="font-mono-tech text-sm text-orange">JULY 2026</div>
            </div>
          </motion.div>

          {/* Venue */}
          <motion.div
            custom={1} variants={fade} initial="hidden" whileInView="show" viewport={{ once: true }}
            className="group relative flex min-h-[220px] flex-col justify-end overflow-hidden rounded-sm border border-white/10 md:col-span-8"
            data-testid="event-venue"
          >
            <img src={CROWD_IMG} alt="" className="absolute inset-0 h-full w-full object-cover opacity-30 transition-transform duration-700 group-hover:scale-105" />
            <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/70 to-transparent" />
            <div className="relative p-8">
              <MapPin className="mb-4 h-7 w-7 text-orange" />
              <div className="overline text-muted-foreground">Venue</div>
              <div className="mt-2 font-display text-2xl font-medium text-white">Arcor Hotels</div>
              <p className="mt-1 max-w-md font-sans text-sm text-muted-foreground">
                1(N), K.R.S. Road Junction, Industrial Area Metagalli, Mysuru.
              </p>
              <a
                href={`https://www.google.com/maps/search/?api=1&query=${mapsQuery}`}
                target="_blank" rel="noopener noreferrer"
                className="mt-4 inline-flex items-center gap-2 font-mono-tech text-xs uppercase tracking-[0.15em] text-orange hover:text-white"
                data-testid="venue-map-link"
              >
                Open in Maps <ExternalLink className="h-3.5 w-3.5" />
              </a>
            </div>
          </motion.div>

          {/* Organized by Nakoda */}
          <motion.div
            custom={2} variants={fade} initial="hidden" whileInView="show" viewport={{ once: true }}
            className="flex flex-col justify-between rounded-sm border border-white/10 bg-card p-8 md:col-span-8"
            data-testid="organizer"
          >
            <div className="overline text-muted-foreground">Organized by</div>
            <div className="mt-6 flex flex-col items-start gap-5 sm:flex-row sm:items-center sm:justify-between">
              <div className="rounded-sm bg-white p-4">
                <img src={NAKODA_LOGO} alt="Nakoda Electronics" className="h-14 w-auto" />
              </div>
              <p className="max-w-xs font-sans text-sm text-muted-foreground">
                <span className="font-medium text-white">NAKODA ELECTRONICS</span> — An ISO
                9001:2015 Certified Company, bringing top surveillance brands to Mysore.
              </p>
            </div>
          </motion.div>

          {/* Contact */}
          <motion.div
            custom={3} variants={fade} initial="hidden" whileInView="show" viewport={{ once: true }}
            className="group flex flex-col justify-between rounded-sm border border-white/10 bg-orange p-8 transition-transform duration-300 hover:-translate-y-1 md:col-span-4"
            data-testid="event-contact"
          >
            <Phone className="h-7 w-7 text-white" />
            <div className="mt-10">
              <div className="overline text-white/70">For more details</div>
              <a href="tel:+919900456780" className="mt-2 block font-display text-2xl font-semibold text-white" data-testid="contact-phone">
                +91 99004 56780
              </a>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
